import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  ButtonIcon,
  Column,
  Columns,
  Divider,
  Hidden,
  IconAI,
  IconArrow,
  Inline,
  Stack,
  Textarea,
} from 'braid-design-system';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from 'react';

import { useIsHomepageGenAIChatDesktopDrawerEnabled } from 'src/store/featureFlags/hooks';

import translations from '../.vocab';
import {
  CareerFeedAIAssistantContentFrame,
  CareerFeedAIAssistantContentFrameMobile,
} from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantContentFrame';
import { CareerFeedMobileModal } from '../CareerFeedAIAssistantContent/CareerFeedMobileModal';
import { PrivacyAndCollectionNotice } from '../CareerFeedAIAssistantContent/PrivacyAndCollectionNotice';
import SocialProofSection from '../CareerFeedAiAssistantInitialState/SocialProofSection/SocialProofSection';
import { CareerFeedMobileWebFeatureFlagContainer } from '../CareerFeedMobileWebFeatureFlagContainer';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';
import type { ErrorType } from '../types';

import { FeedbackComponent } from './FeedbackComponent';
import { OptionsButtons } from './OptionsButtons';
import { useSuggestions } from './useSuggestions';

export type CareerFeedAssistantQuestionSuggestion = {
  label: string;
  nextPrompt: string;
  showFreeTextField?: boolean;
  isSkipButton?: boolean;
};

type CareerFeedAssistantQuestionProps = {
  message: string;
  suggestions: CareerFeedAssistantQuestionSuggestion[];
  isLoading: boolean;
  onSendMessage: (
    userFreeTextMessage: string,
    skip: boolean,
    selectedSuggestions: CareerFeedAssistantQuestionSuggestion[],
  ) => void;
  onSendFeedback: (sentiment: boolean) => void;
  showFreeTextFieldOnly?: boolean;
  sendMessageError?: ErrorType | null;
  progressBar: ComponentProps<
    typeof CareerFeedAIAssistantContentFrame
  >['progressBar'];
};

const CHARACTER_LIMIT_FOR_FREE_TEXT = 280;

type CareerFeedFreeTextInputProps = {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  changeKeys?: readonly unknown[];
};

const CareerFeedFreeTextInput = ({
  onSendMessage,
  isLoading,
  changeKeys = [],
}: CareerFeedFreeTextInputProps) => {
  const { t } = useTranslations(translations);
  const [inputField, setInputField] = useState('');

  useEffect(() => {
    setInputField('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, changeKeys);

  const onSendButtonClick = useCallback(() => {
    if (
      inputField.length > 0 &&
      !isLoading &&
      inputField.length <= CHARACTER_LIMIT_FOR_FREE_TEXT
    ) {
      onSendMessage(inputField);
    }
  }, [inputField, isLoading, onSendMessage]);

  return (
    <Columns space="small" alignY="center">
      <Column>
        <Textarea
          aria-label={t('message')}
          id="career-feed-free-text-area"
          value={inputField}
          onChange={(event) => setInputField(event.currentTarget.value)}
          characterLimit={CHARACTER_LIMIT_FOR_FREE_TEXT}
          disabled={isLoading}
        />
      </Column>
      <Column width="content">
        <ButtonIcon
          icon={<IconArrow direction="up" />}
          label={t('Send')}
          id="send-message"
          onClick={onSendButtonClick}
        />
      </Column>
    </Columns>
  );
};

export const CareerFeedAssistantQuestion = ({
  message,
  suggestions: _suggestions,
  isLoading,
  onSendMessage,
  onSendFeedback,
  showFreeTextFieldOnly,
  sendMessageError,
  progressBar,
}: CareerFeedAssistantQuestionProps) => {
  const { t } = useTranslations(translations);
  const { openDesktopDrawer, setOpenDesktopDrawer, openMobileModal } =
    useCareerFeedAIAssistant();

  const { suggestions, selectedSuggestions } = useSuggestions(_suggestions);

  const showFreeTextField = useMemo(
    () =>
      selectedSuggestions.find((suggestion) => suggestion.showFreeTextField),
    [selectedSuggestions],
  );

  const handleOnCloseDrawer = () => {
    setOpenDesktopDrawer(false);
  };

  const [selectedPositiveFeedback, setSelectedPositiveFeedback] =
    useState(false);
  const [selectedNegativeFeedback, setSelectedNegativeFeedback] =
    useState(false);

  const [sentFeedback, setSentFeedback] = useState(false);
  const { sequence } = useCareerFeedAIAssistant();

  useEffect(() => {
    setSelectedPositiveFeedback(false);
    setSelectedNegativeFeedback(false);
    setSentFeedback(false);
  }, [message]);

  const onNextButtonClick = useCallback(() => {
    if (selectedSuggestions.length) {
      const hasSkipButton = Boolean(
        selectedSuggestions.find(({ isSkipButton }) => isSkipButton),
      );

      onSendMessage('', hasSkipButton, selectedSuggestions);
    }
  }, [selectedSuggestions, onSendMessage]);

  const onSendFreeTextMessage = useCallback(
    (freeTextMessage: string) => {
      onSendMessage(freeTextMessage, false, selectedSuggestions);
    },
    [onSendMessage, selectedSuggestions],
  );

  const onSkipButtonClick = useCallback(() => {
    onSendMessage('', true, selectedSuggestions);
  }, [onSendMessage, selectedSuggestions]);

  const onResumeButtonClick = useCallback(() => {
    setOpenDesktopDrawer(true);
  }, [setOpenDesktopDrawer]);

  const onResumeButtonClickMobile = useCallback(() => {
    openMobileModal();
  }, [openMobileModal]);

  const isFirstQuestion = sequence === 1;

  const isHomepageGenAIChatDesktopDrawerEnabled =
    useIsHomepageGenAIChatDesktopDrawerEnabled();

  return (
    <CareerFeedMobileWebFeatureFlagContainer
      mobileView={
        <>
          {isHomepageGenAIChatDesktopDrawerEnabled ? (
            <CareerFeedAIAssistantContentFrame
              widgetHeadMessage={{
                text: t(
                  'Answer a few questions so we can find the right jobs for you.',
                ),
                size: 'standard',
              }}
              widgetContentSection={null}
              widgetCtaSection={
                <Stack space="small">
                  <Box maxWidth="content">
                    <Hidden above="mobile">
                      <Button
                        tone="brandAccent"
                        icon={<IconAI />}
                        loading={false}
                        onClick={onResumeButtonClickMobile}
                      >
                        {t('Refine recommendations')}
                      </Button>
                    </Hidden>
                  </Box>
                </Stack>
              }
              errorType={sendMessageError}
              socialProofSection={<SocialProofSection />}
              loadingOnWidget={false}
              loadingOnDrawer={false}
            />
          ) : null}
          <CareerFeedMobileModal>
            <Box height="full">
              <CareerFeedAIAssistantContentFrameMobile
                headMessage={message}
                contentSection={
                  <Stack space="medium">
                    {showFreeTextFieldOnly ? (
                      <Actions>
                        <Button
                          size="small"
                          onClick={onSkipButtonClick}
                          variant="ghost"
                          loading={isLoading}
                        >
                          {t('Skip')}
                        </Button>
                      </Actions>
                    ) : null}
                    {suggestions.length ? (
                      <Inline space="xsmall">
                        <OptionsButtons
                          options={suggestions}
                          disableOnClick={isLoading}
                        />
                      </Inline>
                    ) : null}
                    {!showFreeTextFieldOnly && (
                      <FeedbackComponent
                        positiveFeedbackAction={() => {
                          onSendFeedback(true);
                          setSelectedPositiveFeedback(true);
                          setSentFeedback(true);
                        }}
                        negativeFeedbackAction={() => {
                          onSendFeedback(false);
                          setSelectedNegativeFeedback(true);
                          setSentFeedback(true);
                        }}
                        selectedPositiveFeedback={selectedPositiveFeedback}
                        selectedNegativeFeedback={selectedNegativeFeedback}
                        sentFeedback={sentFeedback}
                      />
                    )}
                  </Stack>
                }
                errorType={sendMessageError}
                ctaSection={
                  showFreeTextFieldOnly || showFreeTextField ? (
                    <Stack space="small">
                      <CareerFeedFreeTextInput
                        onSendMessage={onSendFreeTextMessage}
                        isLoading={isLoading}
                        changeKeys={[showFreeTextFieldOnly, showFreeTextField]}
                      />
                      {isFirstQuestion && <PrivacyAndCollectionNotice />}
                    </Stack>
                  ) : (
                    <Stack space="small">
                      <Button
                        tone="brandAccent"
                        variant="soft"
                        loading={isLoading}
                        onClick={onNextButtonClick}
                      >
                        {t('Next')}
                      </Button>
                      {isFirstQuestion && <PrivacyAndCollectionNotice />}
                    </Stack>
                  )
                }
                loading={isLoading}
              />
            </Box>
          </CareerFeedMobileModal>
        </>
      }
      tabletAndAboveView={
        isHomepageGenAIChatDesktopDrawerEnabled ? (
          <CareerFeedAIAssistantContentFrame
            widgetHeadMessage={{
              text: t(
                'Answer a few questions so we can find the right jobs for you.',
              ),
              size: 'standard',
            }}
            widgetContentSection={null}
            widgetCtaSection={
              <Stack space="small">
                <Box maxWidth="content">
                  <Button
                    tone="brandAccent"
                    icon={<IconAI />}
                    onClick={onResumeButtonClick}
                  >
                    {t('Refine recommendations')}
                  </Button>
                </Box>
              </Stack>
            }
            drawerHeadMessage={message}
            drawerContentSection={
              <>
                {showFreeTextFieldOnly ? (
                  <Actions>
                    <Button
                      size="small"
                      onClick={onSkipButtonClick}
                      variant="ghost"
                      loading={isLoading}
                    >
                      {t('Skip')}
                    </Button>
                  </Actions>
                ) : null}
                {suggestions.length ? (
                  <Inline space="xsmall">
                    <OptionsButtons
                      options={suggestions}
                      disableOnClick={isLoading}
                    />
                  </Inline>
                ) : null}
                {!showFreeTextFieldOnly && (
                  <FeedbackComponent
                    positiveFeedbackAction={() => {
                      onSendFeedback(true);
                      setSelectedPositiveFeedback(true);
                      setSentFeedback(true);
                    }}
                    negativeFeedbackAction={() => {
                      onSendFeedback(false);
                      setSelectedNegativeFeedback(true);
                      setSentFeedback(true);
                    }}
                    selectedPositiveFeedback={selectedPositiveFeedback}
                    selectedNegativeFeedback={selectedNegativeFeedback}
                    sentFeedback={sentFeedback}
                  />
                )}
              </>
            }
            loadingOnDrawer={isLoading && openDesktopDrawer}
            loadingOnWidget={false}
            errorType={sendMessageError}
            drawerCtaSection={
              showFreeTextFieldOnly || showFreeTextField ? (
                <Stack space="gutter">
                  <CareerFeedFreeTextInput
                    onSendMessage={onSendFreeTextMessage}
                    isLoading={isLoading}
                    changeKeys={[showFreeTextFieldOnly, showFreeTextField]}
                  />
                  {isFirstQuestion && <PrivacyAndCollectionNotice />}
                </Stack>
              ) : (
                <Box paddingTop="xxlarge">
                  <Stack space="gutter">
                    <Divider />
                    <Button
                      tone="brandAccent"
                      variant="soft"
                      loading={isLoading}
                      onClick={onNextButtonClick}
                    >
                      {t('Next')}
                    </Button>
                    {isFirstQuestion && <PrivacyAndCollectionNotice />}
                  </Stack>
                </Box>
              )
            }
            shouldOpenDrawer={openDesktopDrawer}
            onDrawerClose={handleOnCloseDrawer}
            socialProofSection={<SocialProofSection />}
            progressBar={progressBar}
          />
        ) : (
          <CareerFeedAIAssistantContentFrame
            widgetHeadMessage={message}
            widgetContentSection={
              <>
                {showFreeTextFieldOnly ? (
                  <Actions>
                    <Button
                      size="small"
                      onClick={onSkipButtonClick}
                      variant="ghost"
                      loading={isLoading}
                    >
                      {t('Skip')}
                    </Button>
                  </Actions>
                ) : null}
                {suggestions.length ? (
                  <Inline space="xsmall">
                    <OptionsButtons
                      options={suggestions}
                      disableOnClick={isLoading}
                    />
                  </Inline>
                ) : null}
                {!showFreeTextFieldOnly && (
                  <FeedbackComponent
                    positiveFeedbackAction={() => {
                      onSendFeedback(true);
                      setSelectedPositiveFeedback(true);
                      setSentFeedback(true);
                    }}
                    negativeFeedbackAction={() => {
                      onSendFeedback(false);
                      setSelectedNegativeFeedback(true);
                      setSentFeedback(true);
                    }}
                    selectedPositiveFeedback={selectedPositiveFeedback}
                    selectedNegativeFeedback={selectedNegativeFeedback}
                    sentFeedback={sentFeedback}
                  />
                )}
              </>
            }
            loadingOnDrawer={false}
            loadingOnWidget={isLoading}
            errorType={sendMessageError}
            widgetCtaSection={
              showFreeTextFieldOnly || showFreeTextField ? (
                <Stack space="small">
                  <CareerFeedFreeTextInput
                    onSendMessage={onSendFreeTextMessage}
                    isLoading={isLoading}
                    changeKeys={[showFreeTextFieldOnly, showFreeTextField]}
                  />
                  {isFirstQuestion && <PrivacyAndCollectionNotice />}
                </Stack>
              ) : (
                <Stack space="small">
                  <Box maxWidth="content">
                    <Button
                      tone="brandAccent"
                      variant="soft"
                      loading={isLoading}
                      onClick={onNextButtonClick}
                    >
                      {t('Next')}
                    </Button>
                  </Box>
                  {isFirstQuestion && <PrivacyAndCollectionNotice />}
                </Stack>
              )
            }
            shouldOpenDrawer={false}
          />
        )
      }
    />
  );
};
